import React, { useState, useMemo } from "react";
import Select from "react-select";
import { getRpGpLaborEffortId } from "../../../api/paymentsProgressApi";
import "./EditReportSketchDesignModal.css";

const createEffortOptions = () => {
  const options = [];
  for (let hours = 0; hours <= 8; hours++) {
    for (let minutes = 0; minutes < 60; minutes += 15) {
      if (hours === 0 && minutes === 0) continue;
      if (hours === 8 && minutes > 0) break;
      const value = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
      options.push({ value, label: value });
    }
  }
  return options;
};

const EFFORT_OPTIONS = createEffortOptions();

const secondsSpendToEffort = (secondsSpend) => {
  if (secondsSpend == null) return "00:00";
  const totalMinutes = Math.floor(Number(secondsSpend) / 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const effortToSecondsSpend = (effort) => {
  const [hours, minutes] = effort.split(":").map(Number);
  return hours * 3600 + minutes * 60;
};

const EditReportGeneralDesignModal = ({
  report,
  generalDesignProjects,
  onClose,
  onSave,
  onDelete,
  error,
}) => {
  const [projectId, setProjectId] = useState(report?.project_id ?? "");
  const [effort, setEffort] = useState(
    secondsSpendToEffort(report?.seconds_spend) || "00:15"
  );
  const [comment, setComment] = useState(report?.comment ?? "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const projectOptions = useMemo(
    () =>
      generalDesignProjects.map((p) => ({
        value: p.id,
        label: p.section_name ? `${p.name} — ${p.section_name}` : p.name,
        laborEffortId: getRpGpLaborEffortId(p),
      })),
    [generalDesignProjects]
  );

  const isFormValid = () => projectId && effort && effort !== "00:00";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const selected = projectOptions.find(
        (o) => o.value === parseInt(projectId, 10),
      );
      const payload = {
        id: report.id,
        project_id: parseInt(projectId, 10),
        comment: comment.trim(),
        seconds_spend: effortToSecondsSpend(effort),
      };
      const laborEffortId =
        selected?.laborEffortId ?? getRpGpLaborEffortId(report);
      if (laborEffortId) {
        payload.rp_gp_labor_effort_id = laborEffortId;
      }
      await onSave(payload);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm("Удалить этот отчёт по Генплану РП?") ||
      isDeleting ||
      isSubmitting
    ) {
      return;
    }
    setIsDeleting(true);
    try {
      await onDelete();
    } finally {
      setIsDeleting(false);
    }
  };

  const selectStyles = {
    control: (provided) => ({
      ...provided,
      borderRadius: "10px",
      border: "1px solid #E0E0E0",
      padding: "5px",
      fontFamily: "Manrope",
      fontSize: "14px",
      minHeight: "40px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 10000,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 10000,
    }),
  };

  const reportDate = report?.datetime
    ? new Date(report.datetime).toLocaleDateString("ru-RU", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
    : "-";

  return (
    <div className="edit-report-sketch-modal-overlay" onClick={onClose}>
      <div
        className="edit-report-sketch-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="edit-report-sketch-modal__title">
          Редактировать отчёт (Генплан РП)
        </h2>
        {error && (
          <div className="edit-report-sketch-modal__error">{error}</div>
        )}
        <form onSubmit={handleSubmit} className="edit-report-sketch-modal-form">
          <div className="edit-report-sketch-modal__row">
            <label>Дата</label>
            <span className="edit-report-sketch-modal__readonly">{reportDate}</span>
          </div>
          <div className="edit-report-sketch-modal__row">
            <label>Проект</label>
            <Select
              options={projectOptions}
              value={
                projectOptions.find(
                  (o) => o.value === parseInt(projectId, 10)
                ) ?? null
              }
              onChange={(opt) =>
                setProjectId(opt ? opt.value : "")
              }
              placeholder="Выберите проект"
              styles={selectStyles}
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          <div className="edit-report-sketch-modal__row">
            <label>Часы</label>
            <Select
              options={EFFORT_OPTIONS}
              value={EFFORT_OPTIONS.find((o) => o.value === effort) ?? null}
              onChange={(opt) => setEffort(opt ? opt.value : "00:00")}
              placeholder="00:00"
              styles={{
                ...selectStyles,
                control: (p) => ({
                  ...selectStyles.control(p),
                  width: "120px",
                }),
              }}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          <div className="edit-report-sketch-modal__row">
            <label>Комментарий</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="edit-report-sketch-modal__textarea"
              rows={3}
            />
          </div>
          <div className="edit-report-sketch-modal-buttons">
            <button
              type="button"
              onClick={handleDelete}
              disabled={isSubmitting || isDeleting}
              className="edit-report-sketch-modal__btn edit-report-sketch-modal__btn--danger"
            >
              {isDeleting ? "Удаление..." : "Удалить"}
            </button>
            <div className="edit-report-sketch-modal-buttons__right">
              <button
                type="button"
                onClick={onClose}
                className="edit-report-sketch-modal__btn edit-report-sketch-modal__btn--secondary"
              >
                Отмена
              </button>
              <button
                type="submit"
                disabled={!isFormValid() || isSubmitting || isDeleting}
                className="edit-report-sketch-modal__btn edit-report-sketch-modal__btn--primary"
              >
                {isSubmitting ? "Сохранение..." : "Сохранить"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditReportGeneralDesignModal;
